import { SubmissionError } from '#helper/submission-error';
import {
  APPLIANCE_FETCH_STARTED,
  APPLIANCE_FETCH_SUCCEEDED,
  APPLIANCE_FETCH_FAILED,
  APPLIANCE_CLEAR,
  CLOSE_BACKDROP,
  SUCCESS,
  FETCH_APPLIANCE_EVENTS_AND_LOGS,
  FETCH_APPLIANCE_EVENTS_AND_LOGS_STARTED,
  STOP_APPLIANCE
} from '#action/types';
import { webPortalApi } from '#config/web-portal-api';
import { snackBarError } from '#action/fetch';
import { getRequestOptions, getDateRange } from '#helper';
import { history } from '#configureStore';

let abortController = new AbortController();

export const fetchAppliance = id => async dispatch => {
  try {
    abortController.abort();
    const applianceUrl = webPortalApi.buildApiUrl(`warehouse/appliance/${id}`);
    dispatch({
      type: APPLIANCE_FETCH_STARTED
    });
    abortController = new AbortController();
    const { signal } = abortController;
    const res = await fetch(applianceUrl, { ...getRequestOptions(), signal });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }

    const appliance = await res.json();

    if (!res.ok) {
      const isApplianceNotFound = [403, 404].includes(appliance.resStatus);
      dispatch(
        snackBarError({
          message: 'Error in request',
          ...appliance
        })
      );

      if (isApplianceNotFound) {
        await history.push('/warehouse');
      }
      throw appliance;
    }

    const projectRes = await fetch(webPortalApi.buildApiUrl(`project?modules=true&ids=${appliance.projectId}`), getRequestOptions());
    const [project] = await projectRes.json();
    appliance.project = project;

    dispatch({
      type: APPLIANCE_FETCH_SUCCEEDED,
      payload: appliance
    });
  } catch (e) {
    if (e.name === 'AbortError') {
      return;
    }
    dispatch({
      type: APPLIANCE_FETCH_FAILED,
      error: e
    });
  }
};

export const clear = () => dispatch => {
  abortController.abort();
  dispatch({
    type: APPLIANCE_CLEAR
  });
};

export const createAppliance = appliance => async dispatch => {
  try {
    const applianceUrl = webPortalApi.buildApiUrl('warehouse/appliance');
    const res = await fetch(applianceUrl, {
      method: 'POST',
      body: JSON.stringify(appliance),
      ...getRequestOptions()
    });
    await dispatch({ type: CLOSE_BACKDROP });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }

    const response = await res.json();

    if (!res.ok) {
      dispatch(
        snackBarError({
          message: 'Error in request',
          ...response
        })
      );
      throw new SubmissionError(response);
    }

    dispatch({
      type: SUCCESS,
      payload: {
        message: 'Application has been created'
      }
    });

    return { success: true, id: response.id };
  } catch (e) {
    return { success: false };
  }
};

export const updateAppliance = (applianceId, appliance) => async dispatch => {
  try {
    const applianceUrl = webPortalApi.buildApiUrl(`warehouse/appliance/${applianceId}`);
    const res = await fetch(applianceUrl, {
      method: 'PATCH',
      body: JSON.stringify(appliance),
      ...getRequestOptions()
    });
    await dispatch({ type: CLOSE_BACKDROP });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }
    const response = await res.json();

    if (!res.ok) {
      dispatch(
        snackBarError({
          message: 'Error in request',
          ...response
        })
      );
      throw new SubmissionError(response);
    }

    dispatch({
      type: SUCCESS,
      payload: {
        message: 'Application has been updated'
      }
    });

    return { success: true };
  } catch (e) {
    return { success: false };
  }
};

export const deleteAppliance = applianceId => async dispatch => {
  try {
    const applianceUrl = webPortalApi.buildApiUrl(`warehouse/appliance/${applianceId}`);
    const res = await fetch(applianceUrl, {
      method: 'DELETE',
      ...getRequestOptions()
    });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }
    const response = await res.json();

    if (!res.ok) {
      dispatch(
        snackBarError({
          message: 'Error in request',
          ...response
        })
      );
      throw new SubmissionError(response);
    }

    dispatch({
      type: SUCCESS,
      payload: {
        message: 'Application has been deleted'
      }
    });

    return { success: true };
  } catch (e) {
    return { success: false };
  }
};

export const stopAppliance = (applianceId, stopDate) => async dispatch => {
  try {
    const response = await fetch(webPortalApi.buildApiUrl(`warehouse/appliance/${applianceId}/stop`), {
      method: 'PATCH',
      ...getRequestOptions(),
      body: JSON.stringify(stopDate)
    });
    const data = await response.json();
    if (!response.ok) {
      dispatch(
        snackBarError({
          message: 'Error in request',
          ...data
        })
      );
      return { success: false, error: data };
    }

    dispatch({
      type: STOP_APPLIANCE,
      payload: data
    });

    dispatch({
      type: SUCCESS,
      payload: {
        message: 'Application was stopped!'
      }
    });
    return { success: true };
  } catch (e) {
    dispatch(
      snackBarError({
        message: 'An unexpected error occurred stopping appliance',
        error: e.message
      })
    );
    return { success: false, error: e.message };
  }
};

function getUri(id, period, path, queryParams) {
  let URI = `warehouse/appliance/${id}/${path}`;

  if (period) {
    const { start, end } = period;
    const range = getDateRange(start, end);
    URI += `/${range.start}/${range.end}`;
  }

  if (queryParams) {
    URI += `/?${new URLSearchParams(queryParams)}`;
  }

  return URI;
}

export const fetchEventsAndLogs = (applianceId, period) => async dispatch => {
  try {
    const applianceLogsURL = webPortalApi.buildApiUrl(getUri(applianceId, period, 'logs', { order: 'ASC' }));
    const applianceEventsURL = webPortalApi.buildApiUrl(getUri(applianceId, period, 'events'));
    const applianceSettingsThresholdsURL = webPortalApi.buildApiUrl(getUri(applianceId, null, 'settings/thresholds'));

    dispatch({
      type: FETCH_APPLIANCE_EVENTS_AND_LOGS_STARTED
    });

    const responses = await Promise.all([
      fetch(applianceLogsURL, getRequestOptions()),
      fetch(applianceEventsURL, getRequestOptions()),
      fetch(applianceSettingsThresholdsURL, getRequestOptions())
    ]);

    const notOkResponses = responses.filter(res => !res.ok);
    if (notOkResponses.length) {
      throw await notOkResponses[0].json();
    }

    const [
      logsData,
      eventsData,
      settingsThresholdsData
    ] =
      await Promise.all(responses.map(res => res.json()));

    return dispatch({
      type: FETCH_APPLIANCE_EVENTS_AND_LOGS,
      payload: {
        events: eventsData,
        logs: logsData?.logs,
        settingsThresholds: settingsThresholdsData.logs
      }
    });
  } catch (err) {
    console.error(err);

    // remove the preloader, show the empty components
    dispatch({
      type: FETCH_APPLIANCE_EVENTS_AND_LOGS,
      payload: {
        events: [],
        logs: [],
        settingsThresholds: []
      }
    });

    return dispatch(
      snackBarError({
        message: 'Error in request',
        ...err
      })
    );
  }
};
